import { createTheme, type ThemeOptions } from '@mui/material/styles';
import * as tokens from './tokens/index.js';
import { getComponentOverrides } from './component-overrides.js';
import { layoutTokens } from './layout-tokens.js';

const baseTypography: ThemeOptions['typography'] = {
  fontFamily: tokens.fontFamilies.sans,
  fontSize: 14,
  allVariants: {
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"kern" 1, "tnum" 1',
  },
  h1: { ...tokens.typeScale.display },
  h2: { ...tokens.typeScale.pageTitle },
  h3: { ...tokens.typeScale.sectionTitle },
  h4: { ...tokens.typeScale.panelTitle },
  h5: { ...tokens.typeScale.subsectionTitle },
  h6: { ...tokens.typeScale.compactTitle },
  subtitle1: { ...tokens.typeScale.body, fontWeight: 500 },
  subtitle2: { ...tokens.typeScale.supporting, fontWeight: 500 },
  body1: tokens.typeScale.body,
  body2: tokens.typeScale.supporting,
  button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
  caption: tokens.typeScale.metadata,
  overline: { ...tokens.typeScale.overline, textTransform: 'uppercase' },
};

function getPalette(mode: 'light' | 'dark' = 'light'): ThemeOptions['palette'] {
  const neutral = tokens.palette.neutral;
  const primary = tokens.palette.primary;
  const isDark = mode === 'dark';
  const surface = isDark
    ? {
        page: neutral[900],
        subtle: neutral[800],
        raised: neutral[800],
        interactive: neutral[700],
        sunken: neutral[900],
        overlay: neutral[800],
      }
    : tokens.surface;

  const border = isDark
    ? {
        subtle: neutral[700],
        medium: neutral[600],
        standard: neutral[600],
        strong: neutral[500],
      }
    : tokens.border;

  return {
    mode,
    primary: {
      main: isDark ? primary[300] : primary[600],
      light: isDark ? primary[300] : primary[400],
      dark: isDark ? primary[500] : primary[800],
      contrastText: isDark ? neutral[900] : neutral[0],
    },
    secondary: {
      main: isDark ? primary[300] : primary[700],
      light: isDark ? primary[200] : primary[500],
      dark: isDark ? primary[500] : primary[900],
      contrastText: isDark ? neutral[900] : neutral[0],
    },
    error: {
      main: tokens.palette.error.main,
      light: tokens.palette.error.light,
      dark: tokens.palette.error.dark,
      contrastText: neutral[0],
    },
    warning: {
      main: tokens.palette.warning.main,
      light: tokens.palette.warning.light,
      dark: tokens.palette.warning.dark,
      contrastText: neutral[900],
    },
    info: {
      main: tokens.palette.info.main,
      light: tokens.palette.info.light,
      dark: tokens.palette.info.dark,
      contrastText: neutral[0],
    },
    success: {
      main: tokens.palette.success.main,
      light: tokens.palette.success.light,
      dark: tokens.palette.success.dark,
      contrastText: neutral[900],
    },
    background: {
      default: surface.page,
      paper: surface.raised,
    },
    text: {
      primary: isDark ? neutral[25] : tokens.text.primary,
      secondary: isDark ? neutral[400] : tokens.text.secondary,
      disabled: isDark ? neutral[500] : tokens.text.disabled,
    },
    divider: isDark ? neutral[700] : tokens.border.subtle,
    grey: {
      50: neutral[50],
      100: neutral[100],
      200: neutral[200],
      300: neutral[300],
      400: neutral[400],
      500: neutral[500],
      600: neutral[600],
      700: neutral[700],
      800: neutral[800],
      900: neutral[900],
    },
    finance: {
      surface,
      border,
      status: tokens.STATUS_PRESENTATION,
      card: tokens.card,
      surfaceBorder: tokens.surfaceBorder,
      table: tokens.tableSurface,
    },
  };
}

function getShape(): ThemeOptions['shape'] {
  return {
    borderRadius: tokens.radii.medium,
  };
}

export interface CreateFinanceThemeOptions {
  mode?: 'light' | 'dark';
}

export function createFinanceTheme(
  options: CreateFinanceThemeOptions = {}
): ReturnType<typeof createTheme> {
  const themeOptions: ThemeOptions = {
    palette: getPalette(options.mode),
    typography: baseTypography,
    shape: getShape(),
    spacing: (factor: number) => factor * 8,
    financeLayout: layoutTokens,
    components: getComponentOverrides(),
  };

  return createTheme(themeOptions);
}

export const financeTheme = createFinanceTheme();
